import { supabase } from '../lib/supabase';
import { todayIn } from '../book/model';
import { loadBooksOfWorks, loadCopyFacts, loadDetails, loadPlaces, rows, type Details } from '../shelves/load';
import type { ShelfBook } from '../shelves/model';
import { loadRevisitFacts } from '../queue/revisitLoad';
import { yourReadText } from '../queue/revisit';
import type { PageBook } from './people';

// The author and series pages' books (Discover, Part 2), read with your own sign-in: your books of
// the author's works, or of the series' works, with what the table shows (cover, series and number,
// published, shelf and place, copy, your read).

type Row = Record<string, unknown>;
const one = <T>(x: unknown): T | null => (Array.isArray(x) ? ((x[0] as T) ?? null) : ((x as T) ?? null));
const num = (x: unknown) => (x === null || x === undefined || x === '' ? null : Number(x));

export interface AuthorPageData {
  kind: 'author';
  id: string;
  name: string;
  books: PageBook[];
  /** The reading books' details (the note on the book you're reading, its %). */
  details: Map<string, Details>;
}

export interface SeriesPageData {
  kind: 'series';
  id: string;
  name: string;
  /** The series' author (the one with most of its works), for "by Jo Nesbø". */
  author: { id: string; name: string } | null;
  books: PageBook[];
  details: Map<string, Details>;
}

export class PageNotFound extends Error {}

function db() {
  if (!supabase) throw new Error('Not connected to a database.');
  return supabase;
}

/** Everything the table shows for these books. `seriesOf` picks each work's series entry. */
async function enrich(heads: ShelfBook[], seriesOf: (workId: string) => Row | undefined, workRows: Row[]): Promise<{ books: PageBook[]; details: Map<string, Details> }> {
  const today = todayIn();
  const reading = new Set(heads.filter((b) => b.status === 'currently_reading').map((b) => b.id));
  const [details, copies, places, reads] = await Promise.all([loadDetails(heads, reading), loadCopyFacts(heads, today), loadPlaces(heads), loadRevisitFacts(heads.map((b) => b.id))]);
  const books = heads.map((b): PageBook => {
    const d = details.get(b.id);
    const c = copies.get(b.id);
    const sw = seriesOf(b.workId);
    const w = workRows.find((x) => x.id === b.workId);
    const year = typeof w?.first_published_on === 'string' ? Number(w.first_published_on.slice(0, 4)) : null;
    return {
      ...b,
      ...d,
      owned: c?.owned ?? d?.owned,
      formats: c?.formats ?? [],
      library: c?.library ?? [],
      seriesId: (sw?.series_id as string) ?? null,
      seriesName: sw ? ((one<Row>(sw.series)?.name as string) ?? null) : null,
      seriesPos: sw ? num(sw.position) : null,
      seriesLabel: sw ? ((sw.label as string) ?? null) : null,
      published: year,
      rank: places.get(b.id) ?? null,
      read: yourReadText(b.status, reads.get(b.id), d?.reading?.percent, today),
    };
  });
  return { books, details };
}

/** An author's page: their name, and your books of their works (as author or co-author). */
export async function loadAuthorPage(id: string): Promise<AuthorPageData> {
  const person = (await rows(db().from('people').select('id, name').eq('id', id)))[0];
  if (!person) throw new PageNotFound();
  const credits = await rows(db().from('work_people').select('work_id').eq('person_id', id).in('role', ['author', 'co_author']));
  const works = [...new Set(credits.map((c) => c.work_id as string))];
  const heads = works.length ? await loadBooksOfWorks(works) : [];
  const mine = [...new Set(heads.map((b) => b.workId))];
  const [series, workRows] = mine.length
    ? await Promise.all([
        rows(db().from('series_works').select('work_id, series_id, position, label, series:series(name)').in('work_id', mine)),
        rows(db().from('works').select('id, first_published_on').in('id', mine)),
      ])
    : [[], []];
  // A work in two series: its first by number, as on the shelves.
  const seriesOf = (w: string) => series.filter((s) => s.work_id === w).sort((a, b) => (num(a.position) ?? 0) - (num(b.position) ?? 0))[0];
  return { kind: 'author', id, name: person.name as string, ...(await enrich(heads, seriesOf, workRows)) };
}

/** A series' page: its name, its author, and your books of its works. */
export async function loadSeriesPage(id: string): Promise<SeriesPageData> {
  const series = (await rows(db().from('series').select('id, name').eq('id', id)))[0];
  if (!series) throw new PageNotFound();
  const entries = await rows(db().from('series_works').select('work_id, series_id, position, label').eq('series_id', id));
  const works = [...new Set(entries.map((e) => e.work_id as string))];
  const [heads, credits, workRows] = works.length
    ? await Promise.all([
        loadBooksOfWorks(works),
        rows(db().from('work_people').select('work_id, person_id, person:people(name)').in('work_id', works).in('role', ['author', 'co_author'])),
        rows(db().from('works').select('id, first_published_on').in('id', works)),
      ])
    : [[], [], []];
  // The author with the most of the series' works.
  const tally = new Map<string, { id: string; name: string; n: number }>();
  for (const c of credits) {
    const name = one<Row>(c.person)?.name as string | undefined;
    if (!name) continue;
    const t = tally.get(c.person_id as string) ?? { id: c.person_id as string, name, n: 0 };
    t.n += 1;
    tally.set(t.id, t);
  }
  const author = [...tally.values()].sort((a, b) => b.n - a.n)[0] ?? null;
  const named = { ...series, name: series.name as string };
  const seriesOf = (w: string) => {
    const e = entries.find((x) => x.work_id === w);
    return e ? { ...e, series: { name: named.name } } : undefined;
  };
  return { kind: 'series', id, name: named.name, author: author ? { id: author.id, name: author.name } : null, ...(await enrich(heads, seriesOf, workRows)) };
}
